import { useQuery, useQueryClient } from 'react-query';
import { fetchWrapper, handleResponse } from 'utils/fetchWrapper';
import { SPARK_RESOURCE_TYPES, STATUS_TAGS } from 'constants/common';
import { useTranscriptionStatusTypes } from 'hooks/EntityTypes/useTranscriptionStatusTypes';
import type { SingleTranscriptionResponse } from 'types/Transcriptions';
import React from 'react';

const getTranscriptionQueueJob = (
  transcriptionID: string | undefined | null
) => {
  return fetchWrapper<SingleTranscriptionResponse, true>({
    url: `transcriptions/queue-jobs/${transcriptionID}`,
    method: 'GET',
    rawResponse: true,
  });
};

export function useTranscriptionQueueJob(
  transcriptionID: string | undefined | null,
  errorCallback = () => {}
) {
  const [isError, setIsError] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);
  const [isRunning, setIsRunning] = React.useState(false);
  const queryClient = useQueryClient();
  const { idMap: transcriptionStatusTypeIdMap, statusNameByIdMap } =
    useTranscriptionStatusTypes();

  const response = useQuery(
    ['transcriptionQueueJob', transcriptionID],
    () => getTranscriptionQueueJob(transcriptionID),
    {
      onError: (error) => {
        setIsError(isError);
      },
      onSuccess: async (response) => {
        if (!transcriptionStatusTypeIdMap) {
          return;
        }
        const retryAfter = response.headers.get('Retry-After');
        const data =
          await handleResponse<SingleTranscriptionResponse>(response);

        const transcriptionAttributes = data?.data.attributes;
        const transcriptionStatusTypeID =
          transcriptionAttributes.transcriptionStatusTypeID;

        const isError =
          statusNameByIdMap?.[transcriptionStatusTypeID] === STATUS_TAGS.ERROR;

        const isSuccess =
          statusNameByIdMap?.[transcriptionStatusTypeID] ===
          STATUS_TAGS.SUCCESS;
        const isRunning =
          statusNameByIdMap?.[transcriptionStatusTypeID] ===
          STATUS_TAGS.RUNNING;

        console.log('statuses', {
          isError,
          isSuccess,
          isRunning,
          transcriptionStatusTypeID,
        });

        setIsSuccess(isSuccess);
        setIsError(isError);
        setIsRunning(isRunning);

        if (isSuccess) {
          queryClient.invalidateQueries([
            SPARK_RESOURCE_TYPES.TRANSCRIPTIONS,
            transcriptionAttributes.fileID,
          ]);
          return;
        }

        if (isError) {
          errorCallback?.();
          return;
        }

        setTimeout(
          () => {
            queryClient.invalidateQueries([
              'transcriptionQueueJob',
              transcriptionID,
            ]);
          },
          retryAfter ? parseInt(retryAfter) * 1000 : 1000
        );
      },
      enabled: Boolean(transcriptionID),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  return {
    ...response,
    isRunning,
    isError,
    isSuccess,
  };
}
